import classNames from 'classnames';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { IPredicate, ISegmentUsage, Segment } from '@feathr/blackbox';
import { CampaignClass, ensurePredicateKeys } from '@feathr/blackbox';
import { Alert, Button, CardV2 as Card, Spinner, toast } from '@feathr/components';
import SaveAsNewButton from '@feathr/extender/App/DataSegmentsPage/DataSegmentPage/SaveAsNewButton';
import GroupEditWarning from '@feathr/extender/components/GroupEditWarning';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, logUserEvents, useToggle } from '@feathr/hooks';
import { RerunModal, useFlags } from '@feathr/report_components';

import PredicateList from '../PredicateList';
import {
  addPredicate,
  createDefaultRule,
  writePredicates,
} from '../PredicateList/PredicateList.utils';
import SegmentBuilderAI from '../SegmentBuilderAI';
import GroupModeControls from './GroupModeControls';
import SegmentGauge from './SegmentGauge';

import styles from './EditFilters.css';

interface IProps {
  filterContext?:
    | 'campaign'
    | 'flight'
    | 'segment'
    | 'campaignGoal'
    | 'flightGoal'
    | 'facebookCampaign';
  className?: string;
  hideAlert?: boolean;
  hideGauge?: boolean;
  mode?: 'count' | 'crumbs' | 'reachable' | 'emails';
  segment: Segment;
  disabled?: boolean;
}

function EditFilters({
  className,
  disabled = false,
  filterContext = 'segment',
  hideAlert = false,
  hideGauge = false,
  mode = 'count',
  segment,
}: IProps): JSX.Element {
  const { Conversions } = useStore();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const { t } = useTranslation();
  const [usage, setUsage] = useState<ISegmentUsage>();
  const [isRerunModalOpen, toggleRerunModalOpen] = useToggle(false);
  const [isAIModalOpen, toggleAIModalOpen] = useToggle(false);
  const { aiGroupBuilder } = useFlags();

  // The campaign being edited is excluded from its own "Campaign is" value dropdowns (ValueInput
  // turns this into an id__nin filter): a campaign's audience can't reference the campaign itself.
  // Memoized for a stable identity so memoized predicate rows don't re-render on unrelated
  // re-renders here.
  const excludeIds = useMemo(() => (campaignId ? [campaignId] : undefined), [campaignId]);

  const { canClone, canEdit, isReadOnly } = segment.permissions;
  const isDisabled = isReadOnly || canEdit === false || disabled;

  const showNoPermissionsAlert = !hideAlert && !segment.get('parent_segment') && canEdit === false;
  const predicates = segment.get('predicates') ?? [];

  const getUsage = useCallback(async () => {
    // An ephemeral (unsaved) segment has a client-minted id the backend doesn't own, so
    // GET segments/{id}/usage 404s now that the endpoint is account-scoped (anhinga #7660).
    // It also has no usage yet — set an EMPTY usage instead of fetching. Skipping the fetch
    // is not enough: the `if (!usage)` spinner gate below would never clear and the filter
    // panel (the "Add filter set" button) would never render. Keep fetching when isPending —
    // the segment exists on the backend.
    if (segment.isEphemeral && !segment.isPending) {
      setUsage({ active_campaigns: [], targetings: [], goals: [] });
      return;
    }
    const segmentUsage = await segment.getUsage();
    setUsage(segmentUsage);
  }, [segment]);

  useEffect(() => {
    getUsage();
  }, [getUsage]);

  if (!usage) {
    return <Spinner />;
  }

  const affectedGoals = usage.goals.length;
  const hasFacebook = usage.active_campaigns.some((cpn) => cpn._cls === CampaignClass.Facebook);

  function handleAddFilter(): void {
    addPredicate(segment, createDefaultRule('activity'));
  }

  function handleSegmentGenerated(newPredicates: IPredicate[]): void {
    // AI-generated predicates arrive without keys; mint them before they reach the editor.
    writePredicates(
      segment,
      newPredicates.map((predicate) => ensurePredicateKeys(predicate)),
    );
  }

  async function handleConfirmRerun(): Promise<void> {
    const params = new URLSearchParams({
      segment: segment.id,
    });
    try {
      await Conversions.rerun(params);
      runInAction(() => {
        segment.set({ rerun: true });
      });
    } catch (e) {
      toast(t("Couldn't recalculate conversions. Try again."), {
        type: 'error',
      });
      throw e;
    }
  }

  const permissionsAlertDescription = canClone && (
    <>
      <SaveAsNewButton group={segment} prefix={''} type={'link'}>
        {t('Save as new')}
      </SaveAsNewButton>{' '}
      {t('in order to add additional filters.')}
    </>
  );

  const noPermissionsAlert = showNoPermissionsAlert && (
    <Alert
      description={permissionsAlertDescription}
      title={t("You don't have permissions to edit this group.")}
      type={'info'}
    />
  );

  const AddFilterButton = (
    <Button
      disabled={isDisabled}
      name={'add_filter'}
      onClick={handleAddFilter}
      prefix={getIconForAction('add')}
    >
      {t('Add filter set')}
    </Button>
  );

  function handleOpenAIModal(): void {
    logUserEvents({ clicked_pixl_group_builder: null });
    toggleAIModalOpen();
  }

  const AIFilterButton = aiGroupBuilder && (
    <Button
      disabled={isDisabled}
      name={'ai_filter'}
      onClick={handleOpenAIModal}
      prefix={getIconForAction('magic')}
      suffix={<span className={styles.betaPill}>Beta</span>}
      tooltip={t('Groups generated by Pixl Group Builder use AI and may not be accurate')}
      tooltipPosition={'top-end'}
      type={'recommendationSecondary'}
    >
      {t('Pixl Group builder')}
    </Button>
  );

  return (
    <div className={className}>
      {noPermissionsAlert}
      {!!affectedGoals && !isDisabled && (
        <GroupEditWarning affectedGoals={affectedGoals} onRecalculate={toggleRerunModalOpen} />
      )}

      {isRerunModalOpen && (
        <RerunModal
          context={'Group'}
          id={segment.id}
          name={segment.name}
          onClose={toggleRerunModalOpen}
          onConfirm={handleConfirmRerun}
        />
      )}

      {hasFacebook && (
        <Alert
          title={t(
            'Targets being used by active Meta campaigns currently only support the "URL" filter.',
          )}
          type={'warning'}
        />
      )}

      <Card theme={isDisabled ? 'disabled' : 'default'} width={'full'}>
        <Card.Content padding={'compact'}>
          <GroupModeControls disabled={isDisabled} group={segment} />
        </Card.Content>
        {predicates.length > 0 && (
          <Card.Content
            contentClassName={classNames(styles.content, className)}
            padding={'compact'}
          >
            <PredicateList
              disabled={isDisabled}
              excludeIds={excludeIds}
              glueClassName={styles.glue}
              glueMode={segment.get('mode')}
              model={segment}
              optionsGroup={filterContext}
            />
          </Card.Content>
        )}

        {!isDisabled && (
          <Card.Actions>
            <div className={styles.buttonGroup}>
              {AddFilterButton}
              {AIFilterButton}
            </div>
          </Card.Actions>
        )}
      </Card>

      {!hideGauge && <SegmentGauge mode={mode} segment={segment} />}

      {isAIModalOpen && (
        <SegmentBuilderAI
          isOpen={isAIModalOpen}
          onClose={toggleAIModalOpen}
          onSegmentGenerated={handleSegmentGenerated}
          segment={segment}
        />
      )}
    </div>
  );
}

export default observer(EditFilters);
